import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { AuthUser, TenantBranding } from "@match/shared";
import { ApiError, api, tokens } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Login } from "@/pages/Login";
import { Home } from "@/pages/Home";
import { Accounts } from "@/pages/Accounts";
import { Clients } from "@/pages/Clients";
import { ClientDetail } from "@/pages/ClientDetail";
import { Trade } from "@/pages/Trade";
import { Sessions } from "@/pages/Sessions";
import { Cycles, CycleDetail } from "@/pages/Cycles";
import { SessionWizard } from "@/pages/SessionWizard";
import { SessionDetail } from "@/pages/SessionDetail";
import { Servers } from "@/pages/Servers";
import { Compare } from "@/pages/Compare";
import { Notifications } from "@/pages/Notifications";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // 4xx non si ritenta: un 403 o un 404 non guariscono da soli.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

/** Applica i token colore del tenant sovrascrivendo le variabili CSS. */
function applyBranding(branding: TenantBranding | null) {
  if (!branding?.tokens) return;
  const root = document.documentElement;
  for (const [name, value] of Object.entries(branding.tokens)) {
    const hex = value.dark ?? value.light;
    if (!hex) continue;
    const rgb = hexToRgb(hex);
    if (rgb) root.style.setProperty(`--${name}`, rgb);
  }
}

function hexToRgb(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const b = await api.branding().catch(() => null);
      setBranding(b);
      applyBranding(b);
      if (b?.name) document.title = b.name;

      if (tokens.access) {
        const me = await api.me().catch(() => null);
        if (me) setUser(me);
        else tokens.clear();
      }
      setLoading(false);
    })();
  }, []);

  const tenantName = branding?.name ?? "Match Trading";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page text-sm text-muted">
        Caricamento…
      </div>
    );
  }

  if (!user) return <Login onSignedIn={setUser} tenantName={tenantName} />;

  const signOut = () => {
    // I dati in cache appartengono all'utente che esce.
    queryClient.clear();
    setUser(null);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout user={user} tenantName={tenantName} onSignedOut={signOut} />}>
            <Route index element={<Home />} />
            <Route path="conti" element={<Accounts />} />
            <Route path="clienti" element={<Clients />} />
            <Route path="clienti/:id" element={<ClientDetail />} />
            <Route path="server" element={<Servers />} />
            <Route path="operazione" element={<Trade />} />
            <Route path="cicli" element={<Cycles />} />
            <Route path="cicli/:id" element={<CycleDetail />} />
            <Route path="fasi" element={<Sessions />} />
            <Route path="fasi/nuova" element={<SessionWizard />} />
            <Route path="fasi/:id" element={<SessionDetail />} />
            <Route path="confronto" element={<Compare />} />
            <Route path="impostazioni/notifiche" element={<Notifications />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
